import { Dispatch, RefObject, SetStateAction } from 'react'
import { ReactCropperElement } from 'react-cropper'
import { Box, Divider, IconButton, MenuItem, Select, Typography, useTheme } from '@mui/material'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'

import 'cropperjs/dist/cropper.css'
import './cropped-modal.css'

const aspectRatios = [
  { label: 'Custom', value: 0 },
  { label: 'Square', value: 1 / 1 },
  { label: 'Circle', value: 1 / 1 },
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '3:2', value: 3 / 2 },
  { label: '2:3', value: 2 / 3 },
  { label: '2:1', value: 2 / 1 },
  { label: '1:2', value: 1 / 2 },
]

interface IProps {
  cropperRef: RefObject<ReactCropperElement>
  initialAspectRatio: string
  showRatio?: boolean
  setRroundMode: Dispatch<SetStateAction<boolean>>
}

export const Toolbar = ({
  cropperRef,
  showRatio = false,
  initialAspectRatio,
  setRroundMode,
}: IProps) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        p: '16px 24px',
        rowGap: '20px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '5px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            onClick={() => {
              if (cropperRef.current?.cropper) {
                cropperRef.current?.cropper.zoom(-0.1)
              }
            }}
            sx={{ width: '40px', height: '40px' }}
          >
            <ZoomOutIcon sx={{ width: '30px', height: '30px' }} />
          </IconButton>
          <IconButton
            onClick={() => {
              if (cropperRef.current?.cropper) {
                cropperRef.current?.cropper.zoom(0.1)
              }
            }}
            sx={{ width: '40px', height: '40px' }}
          >
            <ZoomInIcon sx={{ width: '30px', height: '30px' }} />
          </IconButton>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            onClick={() => {
              if (cropperRef.current?.cropper) {
                cropperRef.current?.cropper.rotate(-90)
              }
            }}
            sx={{ width: '40px', height: '40px' }}
          >
            <RotateLeftIcon sx={{ width: '30px', height: '30px' }} />
          </IconButton>
          <IconButton
            onClick={() => {
              if (cropperRef.current?.cropper) {
                cropperRef.current?.cropper.rotate(90)
              }
            }}
            sx={{ width: '40px', height: '40px' }}
          >
            <RotateRightIcon sx={{ width: '30px', height: '30px' }} />
          </IconButton>
        </Box>
      </Box>

      {showRatio && (
        <Box
          sx={{
            display: 'flex',
            gap: '18px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2">Ratio</Typography>

          <Select
            defaultValue={initialAspectRatio}
            onChange={(e) => {
              const [selectedValue, selectedLabel] = (e.target.value as string).split('-')

              // Set roundMode to true if "Circle" is selected based on the label
              if (selectedLabel === 'Circle') {
                setRroundMode(true)
              } else {
                setRroundMode(false)
              }

              if (cropperRef.current?.cropper) {
                cropperRef.current?.cropper.setAspectRatio(Number(selectedValue))
              }
            }}
            size="small"
            SelectDisplayProps={{ style: { paddingTop: '5px', paddingBottom: '5px' } }}
            disableUnderline
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: '160px',
                },
              },
            }}
          >
            {aspectRatios.map((ratio) => (
              <MenuItem key={ratio.label} value={`${ratio.value}-${ratio.label}`}>
                {ratio.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
    </Box>
  )
}
